(function () {
  const PROGRESS_KEY = "quiz:progress";
  const STEP_ACTIVE_CLASS = "quiz__step--active";
  const PROGRESS_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function stateClass(element, state) {
    const base = Array.from(element.classList).find(
      (name) => name.includes("__") && !name.includes("--")
    );

    return base ? `${base}--${state}` : "";
  }

  function toggleState(element, state, on) {
    const name = stateClass(element, state);
    if (name) element.classList.toggle(name, on);
  }

  function hasState(element, state) {
    const name = stateClass(element, state);
    return Boolean(name) && element.classList.contains(name);
  }

  class Quiz {
    constructor(container) {
      this.container = container;
      this.steps = Array.from(container.querySelectorAll("[data-quiz-step]"));
      this.answers = {};
      this.currentIndex = Math.max(
        0,
        this.steps.findIndex((step) => step.classList.contains(STEP_ACTIVE_CLASS))
      );

      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onInput = this.onInput.bind(this);

      this.container.addEventListener("click", this.onClick);
      this.container.addEventListener("submit", this.onSubmit);
      this.container.addEventListener("input", this.onInput);

      this.restoreProgress();
    }

    isDesignMode() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    }

    /* --- Progress -------------------------------------------------------- */

    readProgress() {
      try {
        const saved = JSON.parse(localStorage.getItem(PROGRESS_KEY));
        if (!saved || typeof saved !== "object") return null;
        if (!saved.updatedAt) return null;
        if (Date.now() - saved.updatedAt > PROGRESS_TTL_MS) return null;

        return saved;
      } catch (error) {
        return null;
      }
    }

    writeProgress() {
      if (this.isDesignMode()) return;

      const step = this.steps[this.currentIndex];

      try {
        localStorage.setItem(
          PROGRESS_KEY,
          JSON.stringify({
            step: step ? step.dataset.quizStep : "",
            answers: this.answers,
            updatedAt: Date.now(),
          })
        );
      } catch (error) {
      }
    }

    clearProgress() {
      try {
        localStorage.removeItem(PROGRESS_KEY);
      } catch (error) {
      }
    }

    restoreProgress() {
      const saved = this.readProgress();
      if (!saved) return;

      if (saved.answers && typeof saved.answers === "object") {
        this.answers = saved.answers;
        this.applyAnswers();
      }

      const index = this.steps.findIndex(
        (step) => step.dataset.quizStep === saved.step
      );

      if (index > 0) this.goToStep(index, { persist: false });
    }

    applyAnswers() {
      this.steps.forEach((step) => {
        const saved = this.answers[step.dataset.quizStep];
        const values = Array.isArray(saved) ? saved : saved ? [saved] : [];

        this.options(step).forEach((option) => {
          const picked = values.indexOf(option.dataset.quizValue || "") !== -1;

          toggleState(option, "selected", picked);
          if (option.hasAttribute("aria-checked")) {
            option.setAttribute("aria-checked", picked ? "true" : "false");
          }
        });

        step.querySelectorAll("[data-quiz-field]").forEach((input) => {
          const value = this.answers[input.dataset.quizField];
          if (typeof value === "string") input.value = value;
        });

        this.syncAdvance(step);
      });
    }

    /* --- Steps ----------------------------------------------------------- */

    goToStep(index, options) {
      const step = this.steps[index];
      if (!step) return false;

      this.steps.forEach((item) => item.classList.remove(STEP_ACTIVE_CLASS));
      step.classList.add(STEP_ACTIVE_CLASS);
      this.currentIndex = index;

      step.scrollTop = 0;

      const card = step.querySelector("[data-quiz-card]");
      if (card) card.scrollTop = 0;

      this.syncAdvance(step);

      if (!options || options.persist !== false) this.writeProgress();

      this.emit("step", { name: step.dataset.quizStep, index });

      return true;
    }

    next() {
      return this.goToStep(this.currentIndex + 1);
    }

    prev() {
      return this.goToStep(this.currentIndex - 1);
    }

    /* --- Answers --------------------------------------------------------- */

    options(step) {
      return Array.from(step.querySelectorAll("[data-quiz-option]"));
    }

    chosen(step) {
      return this.options(step)
        .filter((option) => hasState(option, "selected"))
        .map((option) => option.dataset.quizValue || "");
    }

    syncAdvance(step) {
      if (!step.hasAttribute("data-quiz-multiple")) return;

      const advance = step.querySelector("[data-quiz-next]");
      if (advance) advance.disabled = this.chosen(step).length === 0;
    }

    selectOption(button) {
      const step = button.closest("[data-quiz-step]");
      const name = step ? step.dataset.quizStep : "unknown";

      if (step && step.hasAttribute("data-quiz-multiple")) {
        const picked = !hasState(button, "selected");

        toggleState(button, "selected", picked);
        button.setAttribute("aria-checked", picked ? "true" : "false");

        this.answers[name] = this.chosen(step);
        this.syncAdvance(step);
        this.writeProgress();
        this.emit("answer", { step: name, value: this.answers[name] });

        return;
      }

      if (step) {
        this.options(step).forEach((item) => toggleState(item, "selected", false));
      }

      toggleState(button, "selected", true);
      this.answers[name] = button.dataset.quizValue || "";
      this.writeProgress();
      this.emit("answer", { step: name, value: this.answers[name] });

      this.next();
    }

    /* --- Forms ----------------------------------------------------------- */

    showError(form, message) {
      const target = form.querySelector("[data-quiz-error]");
      if (!target) return;

      target.textContent = message;
      toggleState(target, "visible", Boolean(message));
    }

    validateField(input) {
      const value = input.value.trim();

      if (input.required && !value) {
        return `${input.placeholder || "This field"} is required`;
      }

      if (value && input.type === "email" && !EMAIL_PATTERN.test(value)) {
        return "Please enter a valid email address";
      }

      return "";
    }

    submitForm(form) {
      const inputs = Array.from(form.querySelectorAll("[data-quiz-field]"));
      let message = "";

      let firstInvalid = null;

      inputs.forEach((input) => {
        const error = this.validateField(input);
        toggleState(input, "error", Boolean(error));

        if (error && !message) {
          message = error;
          firstInvalid = input;
        }
      });

      if (message) {
        this.showError(form, message);
        if (firstInvalid) firstInvalid.focus({ preventScroll: true });
        return;
      }

      this.showError(form, "");

      inputs.forEach((input) => {
        this.answers[input.dataset.quizField] = input.value.trim();
      });

      const step = form.closest("[data-quiz-step]");
      this.writeProgress();
      this.emit("answer", { step: step ? step.dataset.quizStep : "unknown" });

      this.next();
    }

    /* --- Events ---------------------------------------------------------- */

    emit(name, detail) {
      this.container.dispatchEvent(
        new CustomEvent(`quiz:${name}`, {
          bubbles: true,
          detail: Object.assign({ quiz: this, answers: this.answers }, detail),
        })
      );
    }

    onClick(event) {
      const option = event.target.closest("[data-quiz-option]");
      if (option) {
        this.selectOption(option);
        return;
      }

      if (event.target.closest("[data-quiz-next]")) {
        this.next();
        return;
      }

      if (event.target.closest("[data-quiz-prev]")) {
        this.prev();
      }
    }

    onSubmit(event) {
      const form = event.target.closest("[data-quiz-form]");

      if (!form) {
        /* The plan step's add-to-cart form: the quiz is done, so the next
           visit starts from the beginning instead of resuming. */
        if (event.target.closest('[data-type="add-to-cart-form"]')) {
          this.clearProgress();
        }

        return;
      }

      event.preventDefault();
      this.submitForm(form);
    }

    onInput(event) {
      const input = event.target.closest("[data-quiz-field]");
      if (!input || !hasState(input, "error")) return;

      toggleState(input, "error", false);

      const form = input.closest("[data-quiz-form]");
      const stillInvalid = form
        ? Array.from(form.querySelectorAll("[data-quiz-field]")).some(
            (field) => hasState(field, "error")
          )
        : false;

      if (form && !stillInvalid) {
        this.showError(form, "");
      }
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll("[data-quiz]").forEach((container) => {
      if (container.dataset.quizReady === "true") return;
      container.dataset.quizReady = "true";
      new Quiz(container);
    });
  }

  init();

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
